import classNames from 'classnames';
import React, { KeyboardEvent, useEffect, useState } from 'react';
import { Form, Input, InputOnChangeData } from 'semantic-ui-react';
import MaxLengthInfo from '../MaxLengthInfo';
import POSRemovePill from '../POSRemovePill';
import './POSMultiTag.less';

interface Props {
  items: string[];
  onChange?: (tags: string[]) => void;
  disabled?: boolean;
  showBorder?: boolean;
  required?: boolean;
  addTagOnBlur?: boolean;
  error?: boolean;
  pattern?: RegExp;
  characterLimit?: number;
  maximumTagsAllowed?: number;
  isInlineTags?: boolean;
  placeholder?: string;
  errorText?: string;
  charLimitErrorText?: string;
  label?: string;
  allowSpacing?: boolean;
}
type TagValidateType = boolean | string | RegExpMatchArray;

export const POSMultiTag: React.VFC<Props> = ({
  items = [],
  onChange,
  showBorder = true,
  disabled = false,
  required,
  addTagOnBlur = false,
  error,
  pattern = new RegExp(/^[A-Za-z0-9_-][A-Za-z0-9_-\s]*$/),
  characterLimit,
  maximumTagsAllowed,
  isInlineTags = true,
  placeholder = 'Type and press enter',
  errorText = 'Please enter valid/unique values',
  charLimitErrorText = `Maximum ${characterLimit} characters allowed`,
  label,
  allowSpacing,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const [tagName, setTagName] = useState<string>('');
  const [isValidTag, setValidTag] = useState(true);
  const [showCharLimitError, setShowCharLimitError] = useState(false);

  const onInputChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
  ) => {
    setTagName(data.value);
    if (data.value === '') {
      setValidTag(true);
    }
  };

  const onTagInputBlur = () => {
    if (addTagOnBlur) processTag();
  };

  const processTag = () => {
    setShowCharLimitError(false);
    const tag = tagName.trim();

    if (!tag) return;

    const isValid =
      tag && ((pattern && isRegex(pattern) && tag.match(pattern)) || !pattern);

    const isUniqueTag = !(
      tag &&
      tags.find(
        (x: string) => x.toLocaleLowerCase() === tag.toLocaleLowerCase()
      )
    );
    checkIsTagValidAndUnique(isValid, isUniqueTag, tag);

    setValidTag(isUniqueTag);
  };

  const checkIsValidTag = (isValid: TagValidateType) => {
    if (!isValid) {
      setTagName('');
      setValidTag(true);
      return false;
    }
    return true;
  };

  const checkIsTagExceedsCharLimit = (
    isValid: TagValidateType,
    tag: string
  ) => {
    if (
      checkIsValidTag(isValid) &&
      characterLimit &&
      tag.length > characterLimit
    ) {
      setShowCharLimitError(true);
      return false;
    }
    return true;
  };
  const checkIsTagValidAndUnique = (
    isValid: TagValidateType,
    isUniqueTag: boolean,
    tag: string
  ) => {
    if (checkIsTagExceedsCharLimit(isValid, tag) && isValid && isUniqueTag) {
      updateTags(tag);
    }
  };

  const updateTags = (tag: string) => {
    const parsedTags = [...tags, tag];

    if (
      !maximumTagsAllowed ||
      (maximumTagsAllowed && tags.length < maximumTagsAllowed)
    ) {
      setTags(parsedTags);
    }
    setTagName('');
    onChange && onChange(parsedTags);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || (!allowSpacing && event.key === ' ')) {
      event.preventDefault();
      processTag();
    }
  };

  const isRegex = (value: unknown): value is RegExp => value instanceof RegExp;

  const removeTag = (tag: string) => {
    const newTags = tags.filter((x: string) => x !== tag);
    setTags(tags.filter((x: string) => x !== tag));
    setTagName('');
    onChange && onChange(newTags);
  };

  useEffect(() => {
    setTags(items);
  }, [items]);

  return (
    <div className="p-os-multi-tag-container">
      <div
        className={classNames('p-os-multi-tag', {
          'show-border': showBorder,
          error: error,
        })}
      >
        <div className="p-os-multi-tag-pills">
          {isInlineTags &&
            tags.map((tag: string, index: number) => (
              <POSRemovePill
                disabled={disabled}
                key={index}
                onClick={() => {
                  removeTag(tag);
                }}
                secondary
              >
                {tag}
              </POSRemovePill>
            ))}
          <Form.Field required={required}>
            <MaxLengthInfo
              maxLength={tagName ? characterLimit : maximumTagsAllowed}
              postfix={tagName ? 'Characters' : 'Tags'}
              value={tagName ? tagName : tags}
            >
              <>
                {label && (
                  <label
                    className={classNames({ 'p-os-multi-tag-label': disabled })}
                  >
                    {label}
                  </label>
                )}
                <Input
                  disabled={disabled}
                  error={error || !isValidTag}
                  className={classNames('p-os-multi-tag-input', {
                    'p-os-multi-select-tag': !isInlineTags,
                  })}
                  placeholder={`${placeholder}${required ? '*' : ''}`}
                  value={tagName}
                  onChange={onInputChange}
                  onBlur={onTagInputBlur}
                  onKeyDown={onKeyDown}
                  maxLength={characterLimit}
                />
              </>
            </MaxLengthInfo>
          </Form.Field>
        </div>
      </div>
      <div className="p-os-removable-pills">
        {!isInlineTags &&
          tags.map((tag: string, index: number) => (
            <POSRemovePill
              disabled={disabled}
              key={index}
              onClick={() => {
                removeTag(tag);
              }}
              secondary
            >
              {tag}
            </POSRemovePill>
          ))}
      </div>
      {tagName.length > 0 && (!isValidTag || error) && (
        <div className="error-label">{errorText}</div>
      )}
      {showCharLimitError && (
        <div className="error-label">
          <div className="error-label">{charLimitErrorText}</div>
        </div>
      )}
    </div>
  );
};
